import { safeAuthRedirect } from '../utils/authRedirect';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getApiBase, setActingOrganizationId } from '../utils/api';
import OrganizationSelector from './OrganizationSelector';
import SSOEmailConfirmation from './SSOEmailConfirmation';

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState('');
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [processing, setProcessing] = useState(true);
  const started = useRef(false);

  // Multi-org selection state
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [orgSelectionData, setOrgSelectionData] = useState(null);

  const completeLoginWithUserData = async (responseData) => {
    if (responseData?.requires_email_confirmation) {
      setConfirmEmail(true);
      setProcessing(false);
      return;
    }
    setConfirmEmail(false);
    if (responseData?.organization_id) setActingOrganizationId(responseData.organization_id);
    const apiBase = getApiBase();

    // Fetch complete user data from /auth/me to get all fields including admin_permissions
    try {
      const authResponse = await axios.get(`${apiBase}/auth/me`, {
        withCredentials: true
      });

      if (!authResponse.data?.user) throw new Error("Missing authenticated user");
      if (authResponse.data && authResponse.data.user) {
        const userPayload = authResponse.data.user;

        // Fetch user preferences for theme settings
        try {
          const prefs = (await axios.get(`${apiBase}/users/me/preferences`, {
            withCredentials: true
          })).data;
          userPayload.ui_preferences = prefs;
        } catch (prefError) {
          console.warn('Could not fetch user preferences:', prefError);
        }

        sessionStorage.setItem('user', JSON.stringify(userPayload));
        if (setUser) {
          setUser(userPayload);
        }
      }
    } catch (authMeError) {
      sessionStorage.removeItem('user');
      if (setUser) setUser(null);
      setError('Your browser session could not be established. Please sign in again.');
      setProcessing(false);
      return;
    }

    // Check for returnUrl stored by Login component
    const storedReturnUrl = sessionStorage.getItem('authReturnUrl');
    sessionStorage.removeItem('authReturnUrl');
    navigate(safeAuthRedirect(storedReturnUrl), { replace: true });
  };

  useEffect(() => {
    const handleCallback = async () => {
      if (started.current) return;
      started.current = true;
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      // Remove one-time credentials before subsequent network requests.
      window.history.replaceState(null, '', window.location.pathname);
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError('Google authentication was cancelled or failed.');
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code || !state) {
        setError('Login expired or invalid. Please start again.');
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const apiBase = getApiBase();

        const response = await axios.post(`${apiBase}/auth/google/callback`, { code, state }, {
          withCredentials: true
        });

        // Check if org selection is required (multi-org user)
        if (response.data.requires_org_selection) {
          setOrgSelectionData({
            eligibleOrganizations: response.data.eligible_organizations,
            email: response.data.email
          });
          setShowOrgSelector(true);
          setProcessing(false);
          return;
        }

        // Single org - complete login directly
        await completeLoginWithUserData(response.data);
      } catch (err) {
        console.error('Google OAuth error:', err.response?.status, err.response?.data?.detail || err.message);

        let errorMessage = err.response?.data?.detail || 'Authentication failed. Please try again.';

        if (err.response?.status === 500 && err.response?.data?.detail?.includes('REDIRECT_URI')) {
          errorMessage = 'OAuth configuration error. Please check server logs and ensure GOOGLE_REDIRECT_URI is set correctly.';
        }

        setError(errorMessage);
        setProcessing(false);
        setTimeout(() => navigate('/login'), 5000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser]);

  const handleOrgSelectionSuccess = async (responseData) => {
    setShowOrgSelector(false);
    setProcessing(true);
    await completeLoginWithUserData(responseData);
  };

  const handleOrgSelectionError = (errorMsg) => {
    setError(errorMsg);
    setShowOrgSelector(false);
  };

  const handleOrgSelectionCancel = () => {
    setShowOrgSelector(false);
    navigate('/login', { replace: true });
  };

  return (
    <>
      {/* Organization Selector Modal */}
      {showOrgSelector && orgSelectionData && (
        <OrganizationSelector
          eligibleOrganizations={orgSelectionData.eligibleOrganizations}
          selectionToken={orgSelectionData.selectionToken}
          email={orgSelectionData.email}
          onSuccess={handleOrgSelectionSuccess}
          onError={handleOrgSelectionError}
          onCancel={handleOrgSelectionCancel}
        />
      )}

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg text-center">
          {confirmEmail ? <SSOEmailConfirmation onSuccess={completeLoginWithUserData} onCancel={() => navigate('/login', { replace: true })} /> : processing && !error ? (
            <>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Signing you in with Google...
              </h2>
              <p className="text-gray-600">
                Please wait while we complete authentication
              </p>
            </>
          ) : error ? (
            <>
              <div className="text-red-600 text-6xl mb-4">&#9888;&#65039;</div>
              <h2 className="text-2xl font-bold text-gray-900">
                Authentication Error
              </h2>
              <p className="text-red-600 mt-4">
                {error}
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Redirecting to login page...
              </p>
            </>
          ) : showOrgSelector ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900">
                Select Organization
              </h2>
              <p className="text-gray-600">
                Please choose which organization to sign in to
              </p>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default GoogleCallback;
